import React from "react";
import { Routes, Route } from "react-router-dom";
import { CircularProgress, Box } from "@mui/material";

import AppProviders from "./AppProviders";
import LeftMenu from "./LeftMenu/LeftMenu";
import CenterContent from "./CenterContent";
import { useMobileScreen } from "../lib/hooks";

// routes
const User = React.lazy(() => import("../routes/Admin/User/User"));
const UserManagement = React.lazy(() => import("../routes/Admin/UserManagement/UserManagement"));
const Dashboard = React.lazy(() => import("../routes/Dashboard"));
const AppConfig = React.lazy(() => import("../routes/Admin/AppConfig/AppConfig"));
const Workspace = React.lazy(() => import("../routes/Admin/Workspace"));

// race config routes
const RaceConfigList = React.lazy(() => import("../routes/RaceConfig/RaceConfigList"));
const RaceConfigDetail = React.lazy(() => import("../routes/RaceConfig/RaceConfigDetail"));
const NewRaceConfig = React.lazy(() => import("../routes/RaceConfig/NewRaceConfig"));
const UpdateRaceConfig = React.lazy(() => import("../routes/RaceConfig/UpdateRaceConfig"));
const SetRaceMerchandises = React.lazy(() => import("../routes/RaceConfig/SetRaceMerchandises"));
const MastersList = React.lazy(() => import("../routes/MastersList/MastersList"));
const MerchandiseList = React.lazy(() => import("../routes/Merchandise/MerchandiseList"));
const NewMerchandise = React.lazy(() => import("../routes/Merchandise/NewMerchandise"));
const UpdateMerchandise = React.lazy(() => import("../routes/Merchandise/UpdateMerchandise"));

// registration routes
const RegistrationWizard = React.lazy(() => import("../routes/Registration/Wizard/RegistrationWizard"));
const TeamWizard = React.lazy(() => import("../routes/Registration/Team/TeamWizard"));
const VoucherInput = React.lazy(() => import("../routes/Registration/VoucherInput"));
const PaymentStatus = React.lazy(() => import("../routes/Registration/PaymentStatus"));
const SudoEditRegistration = React.lazy(() => import("../routes/Registration/SudoEdit/SudoEditRegistration"));
const EditContactRegistration = React.lazy(() => import("../routes/Registration/EditContact/EditContactRegistration"));
const EditContactUser = React.lazy(() => import("../routes/Admin/User/EditContactUser"));
const EditProfileUser = React.lazy(() => import("../routes/Admin/User/EditProfileUser"));
const StartList = React.lazy(() => import("../routes/Registration/StartList"));
const TeamStartList = React.lazy(() => import("../routes/Registration/Team/TeamStartList"));

// marketplace routes
const Marketplace = React.lazy(() => import("../routes/Marketplace/Marketplace"));
const SellOffer = React.lazy(() => import("../routes/Marketplace/SellOffer"));
const MarketplacePaymentStatus = React.lazy(() => import("../routes/Marketplace/MarketplacePaymentStatus"));

// runner profile routes
const RunnerProfile = React.lazy(() => import("../routes/Runner/RunnerProfile"));
const Accreditation = React.lazy(() => import("../routes/Accreditation/Accreditation"));
const Photos = React.lazy(() => import("../routes/Photos/Photos"));
const Gallery = React.lazy(() => import("../routes/Gallery"));

// support route (accessible to all)
const Support = React.lazy(() => import("../routes/Support/Support"));

function RouteFeedback() {
  return (
    <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "50%" }}>
      <CircularProgress />
    </Box>
  );
}

function App() {
  const { isMobile } = useMobileScreen();
  const [open, setOpen] = React.useState(() => !isMobile);
  const toggleDrawer = () => {
    setOpen(!open);
  };

  return (
    <AppProviders>
      <LeftMenu open={open} toggleDrawer={toggleDrawer} />

      <CenterContent open={open} toggleDrawer={toggleDrawer}>
        <React.Suspense fallback={<RouteFeedback />}>
          <Routes>
            {/* default routes */}
            <Route path="/user" element={<User />} />
            <Route path="/user/editContact" element={<EditContactUser />} />
            <Route path="/user/editProfile" element={<EditProfileUser />} />
            <Route path="userManagement" element={<UserManagement />} />
            <Route path="appConfig" element={<AppConfig />} />
            <Route path="workspace" element={<Workspace />} />

            {/* race config routes */}
            <Route path="races" element={<RaceConfigList />} />
            <Route path="races/detail" element={<RaceConfigDetail />} />
            <Route path="races/create" element={<NewRaceConfig />} />
            <Route path="races/update" element={<UpdateRaceConfig />} />
            <Route path="races/merchandise" element={<SetRaceMerchandises />} />

            {/* masters list routes */}
            <Route path="masters" element={<MastersList />} />

            {/* merchandise catalog */}
            <Route path="merchandise" element={<MerchandiseList />} />
            <Route path="merchandise/create" element={<NewMerchandise />} />
            <Route path="merchandise/update" element={<UpdateMerchandise />} />

            {/* registration routes */}
            <Route path="register/new" element={<RegistrationWizard mode="new" />} />
            <Route path="register/voucher" element={<VoucherInput />} />
            <Route path="register/paid" element={<PaymentStatus />} />
            <Route path="register/detail" element={<RegistrationWizard mode="detail" />} />
            <Route path="team/detail" element={<TeamWizard />} />
            <Route path="register/edit" element={<RegistrationWizard mode="edit" />} />
            <Route path="register/submitMarket" element={<RegistrationWizard mode="submitMarket" />} />
            <Route path="register/sudoEdit" element={<SudoEditRegistration />} />
            <Route path="register/editContact" element={<EditContactRegistration />} />
            <Route path="register/startList" element={<StartList />} />
            <Route path="team/startList" element={<TeamStartList />} />

            {/* marketplace routes */}
            <Route path="marketplace" element={<Marketplace />} />
            <Route path="marketplace/sell" element={<SellOffer />} />
            <Route path="marketplace/paid" element={<MarketplacePaymentStatus />} />

            {/* runner profile routes */}
            <Route path="runnerProfile" element={<RunnerProfile />} />
            <Route path="accreditation" element={<Accreditation />} />
            <Route path="photos" element={<Photos />} />
            <Route path="gallery" element={<Gallery />} />

            {/* support (accessible to all) */}
            <Route path="support" element={<Support />} />

            {/* catch-all */}
            <Route path="/*" element={<Dashboard />} />
          </Routes>
        </React.Suspense>
      </CenterContent>
    </AppProviders>
  );
}

export default App;
